import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { api, type TechnicianProfile } from '../lib/api'
import { ProfileView } from '../components/ProfileView'
import { RangePicker, type RangeValue } from '../components/RangePicker'
import { PhoneLink, EmailLink } from '../components/links'

export function TechnicianProfilePage() {
  const { id = '' } = useParams()
  const [range, setRange] = useState<RangeValue>({ range: 'year', from: '', to: '' })
  const [data, setData] = useState<TechnicianProfile | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading')
  const [editing, setEditing] = useState(false)

  const load = useCallback(async () => {
    try {
      const d = await api.getTechnicianProfile(id, range.range, range.from, range.to)
      setData(d)
      setState('ok')
    } catch {
      setState('error')
    }
  }, [id, range])

  useEffect(() => {
    void load()
  }, [load])

  if (state === 'loading' && !data) return <div style={{ color: 'var(--gray)' }}>Loading…</div>
  if (state === 'error' || !data) return <div className="page-title">Technician not found</div>

  const t = data.technician
  return (
    <ProfileView
      backTo="/app/dashboard"
      backLabel="Back to dashboard"
      title={t.name}
      subtitle={`Technician${t.active ? '' : ' · inactive'}`}
      headerRight={
        !editing && (
          <button className="link-btn" onClick={() => setEditing(true)}>Edit contact</button>
        )
      }
      filter={<RangePicker value={range} onChange={setRange} />}
      contact={[
        { label: 'Phone', value: <PhoneLink phone={t.phone} /> },
        { label: 'Email', value: <EmailLink email={t.email} /> },
      ]}
      contactEditor={
        editing ? (
          <ContactEditor
            tech={t}
            onCancel={() => setEditing(false)}
            onSaved={async () => { setEditing(false); await load() }}
          />
        ) : undefined
      }
      stats={[
        { label: 'Total cases', value: String(data.stats.total) },
        { label: 'Open', value: String(data.stats.open), accent: 'var(--status-open)' },
        { label: 'In Progress', value: String(data.stats.in_progress), accent: 'var(--status-in-progress)' },
        { label: 'Closed', value: String(data.stats.closed), accent: 'var(--status-closed)' },
      ]}
      cases={data.cases}
    />
  )
}

function ContactEditor({
  tech,
  onCancel,
  onSaved,
}: {
  tech: { id: string; name: string; phone: string | null; email: string | null }
  onCancel: () => void
  onSaved: () => void | Promise<void>
}) {
  const [name, setName] = useState(tech.name)
  const [phone, setPhone] = useState(tech.phone ?? '')
  const [email, setEmail] = useState(tech.email ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.updateTechnician(tech.id, { name: name.trim(), phone, email })
      await onSaved()
    } catch {
      setError('Could not save. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <div style={{ fontWeight: 700, marginBottom: 12 }}>Edit contact</div>
      <div className="field"><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} required /></div>
      <div className="field"><label>Phone</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
      <div className="field"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      {error && <p className="form-error">{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn" style={{ border: '1px solid var(--line)' }} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}
